import Link from 'next/link';
import { ChevronDown, ChevronRight, ChevronUp, File, Folder, Share2 } from 'lucide-react';
import { displayName, extensionOf, humanSize, inlineByteSize, typeLabel } from '../../../lib/artifact-file';
import { fileTypeKind } from '../../../lib/artifact-image';
import { formatConversationDate } from '../../../lib/conversations';
import { creatorOf, folderCountLabel, folderWorkspaceName, pathSegments, type FileFolderRow } from '../../../lib/file-browser';
import { FILE_SORT_KEYS, nextSort, type FileSort, type FileSortKey } from '../../../lib/files-sort';
import { useTranslation } from '../../../lib/i18n';
import { FileCreatedBy } from '../FileCreatedBy';
import { FilesSpaceMark } from './FilesSpaceMark';
import { IconButton, OverflowMenu, type OverflowMenuAction } from '../ui';
import styles from './FilesTable.module.css';
import type { FilesRow } from './files-row';

// The Files list, as rows. ONE table for both Files surfaces — the cross-workspace page and a workspace's own
// gallery — because they are the same list with a different root, and when they were two components they drifted
// (different columns, different sorting, only one had the workspace name).
//
// **Column order is File name → Created by → Date → Type → Size**, and every one of them sorts. A header that
// looks like its neighbours and does nothing when clicked is worse than no affordance; the rules for what sorts
// where, and where the empty values go, live in `lib/files-sort.ts`.
//
// "Created by" and "Date" are DATA-GATED: each renders only when at least one file in scope carries that datum,
// so a list of only pre-attribution files hides the column instead of printing a wall of em-dashes.
//
// The row is a FLEX row, not a grid: secondary columns drop one at a time as the card narrows (the container
// queries in the stylesheet), and a hidden grid item would leave its track behind rather than give the name back
// the width. Below the narrow breakpoint the row becomes two stacked lines — name over meta — which is what makes
// this table work on a phone without a second mobile component to keep in step.

export function FilesTable({
  rows,
  folders,
  onOpenFolder,
  workspaceNames = {},
  showFolderWorkspace = false,
  showFileColumns = true,
  sort,
  onSortChange,
  showCreatedBy,
  showDate,
  onOpen,
  onShare,
  actionsFor,
  onRowMenu,
  selectedName,
}: {
  rows: FilesRow[];
  /** The current folder's subfolders. Rendered by the TABLE, under its header — a folder is a row of this list,
   *  and rendering them above the table put the column titles halfway down the card, under the folders. */
  folders: FileFolderRow[];
  onOpenFolder: (path: string) => void;
  workspaceNames?: Record<string, string>;
  /** Name each folder's owning Space — the merged root only. */
  showFolderWorkspace?: boolean;
  /** Draw the columns only a FILE can fill (Type, Size). False when the listing has no file rows at all — the
   *  merged root of an account that keeps everything in folders is four empty columns otherwise, and a header
   *  that promises data no row on screen can supply. */
  showFileColumns?: boolean;
  sort: FileSort;
  onSortChange: (sort: FileSort) => void;
  showCreatedBy: boolean;
  showDate: boolean;
  /** Opens the file. The row is still a real link underneath (see `href`), so this is the plain-click path. */
  onOpen: (row: FilesRow) => void;
  onShare: (row: FilesRow) => void;
  actionsFor: (row: FilesRow) => OverflowMenuAction[][];
  onRowMenu?: (row: FilesRow) => void;
  /** The file whose card is open beside the table — its row reads as selected. */
  selectedName?: string;
}) {
  const { t } = useTranslation();

  const header = (key: FileSortKey, label: string, className: string) => (
    <span className={className}>
      <SortHeader label={label} active={sort.key === key} direction={sort.direction} onClick={() => onSortChange(nextSort(sort, key))} />
    </span>
  );

  return (
    <div className={styles.table} role="table">
      <div className={styles.headerRow} role="row">
        {header('name', t('agentCreations.table.fileName'), styles.nameCol)}
        {showCreatedBy && header('createdBy', t('files.createdBy'), `${styles.col} ${styles.colWide}`)}
        {showDate && header('date', t('agentCreations.table.date'), `${styles.col} ${styles.date}`)}
        {showFileColumns && header('type', t('agentCreations.detail.type'), `${styles.col} ${styles.type}`)}
        {showFileColumns && header('size', t('agentCreations.detail.size'), `${styles.col} ${styles.size}`)}
        {/* Last of the columns, against the actions. It is NAMED like the rest — an unlabelled column of badges
            leaves the reader to work out what they are. */}
        {showFolderWorkspace && (
          <span className={`${styles.space} ${styles.spaceHeader}`} role="columnheader">
            {t('packs.section.workspace')}
          </span>
        )}
        <span className={styles.actions} aria-hidden />
      </div>
      {folders.map((folder) => (
        <FolderTableRow
          key={folder.path}
          folder={folder}
          workspaceNames={workspaceNames}
          showWorkspace={showFolderWorkspace}
          showCreatedBy={showCreatedBy}
          showDate={showDate}
          showFileColumns={showFileColumns}
          onOpen={onOpenFolder}
        />
      ))}

      {rows.map((row) => {
        const size = inlineByteSize(row.item);
        const type = row.item.isApp ? null : typeLabel(extensionOf(row.item));
        const creator = creatorOf(row.item);
        const selected = selectedName != null && row.item.name === selectedName;
        return (
          <Link
            key={row.key}
            href={row.href}
            as={row.as}
            className={styles.row}
            role="row"
            data-selected={selected || undefined}
            /* A click on the row opens the file, right here — the thing the row is obviously for. It stays a
               real link underneath, so cmd/middle-click and "open in new tab" keep working and the status bar
               shows a destination; only the plain left-click is intercepted. */
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
              e.preventDefault();
              onOpen(row);
            }}
          >
            <span className={styles.name}>
              <span className={styles.icon}>
                <img
                  src={`${row.basePath}/file-types/${fileTypeKind(extensionOf(row.item))}.png`}
                  alt=""
                  width={16}
                  height={20}
                  style={{ objectFit: 'contain' }}
                />
              </span>
              <span className={styles.nameStack}>
                <span className={styles.nameText}>{displayName(row.item)}</span>
                {/* WHERE the file lives. With folders in the picture the workspace alone stops being an answer
                    once two folders hold a file of the same name. */}
                {row.caption && <span className={styles.caption}>{row.caption}</span>}
                {/* The columns' own facts, restated inline for the width where the columns are gone. Hidden by
                    the stylesheet at every width that still has them, so nothing is said twice. */}
                <span className={styles.metaLine}>
                  {creator && <FileCreatedBy creator={creator} size={14} />}
                  <span className={styles.metaFacts}>
                    {[
                      type,
                      size != null ? humanSize(size) : null,
                      row.item.updatedDate ? formatConversationDate(row.item.updatedDate) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </span>
            </span>
            {showCreatedBy && (
              <span className={`${styles.col} ${styles.colWide} ${styles.createdBy}`}>
                {creator ? <FileCreatedBy creator={creator} /> : <span className={styles.muted}>—</span>}
              </span>
            )}
            {showDate && (
              <span className={`${styles.col} ${styles.date} ${styles.muted}`}>
                {row.item.updatedDate ? formatConversationDate(row.item.updatedDate) : '—'}
              </span>
            )}
            {showFileColumns && <span className={`${styles.col} ${styles.type} ${styles.muted}`}>{type ?? '—'}</span>}
            {showFileColumns && (
              <span className={`${styles.col} ${styles.size} ${styles.muted}`}>{size != null ? humanSize(size) : '—'}</span>
            )}
            {/* The last column before the actions, in a fixed slot of its own so both kinds of row put their
                badge at the same x. */}
            {showFolderWorkspace && (
              <span className={styles.space}>
                <FilesSpaceMark id={row.item.workspaceId} name={row.workspaceName} />
              </span>
            )}
            <span className={styles.actions}>
              {/* Share sits OUTSIDE the overflow: it is the action a file row is most often opened for, and a
                  glyph beside the dots costs nothing. Everything else stays in the menu. */}
              <IconButton
                size="small"
                aria-label={t('files.share.title')}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShare(row);
                }}
              >
                <Share2 size={16} aria-hidden />
              </IconButton>
              <OverflowMenu groups={actionsFor(row)} ariaLabel={t('agentCreations.table.options')} onOpen={() => onRowMenu?.(row)} />
            </span>
          </Link>
        );
      })}
    </div>
  );
}

/** A sortable column header. Not a DS Button: it is a table header that carries a direction glyph, and the
 *  button skins would put a box around every column title. */
function SortHeader({
  label,
  active,
  direction,
  onClick,
}: {
  label: string;
  active: boolean;
  direction: FileSort['direction'];
  onClick: () => void;
}) {
  return (
    <span
      className={styles.sortHeader}
      role="columnheader"
      tabIndex={0}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <span className={styles.headTitle}>{label}</span>
      {active && (direction === 'asc' ? <ChevronUp size={13} aria-hidden /> : <ChevronDown size={13} aria-hidden />)}
    </span>
  );
}

/** Exported for the guard test: every sort key has a column, and every column sorts. */
export const SORTABLE_COLUMNS = FILE_SORT_KEYS;

/**
 * A folder, as a row of the SAME table the files are in: it takes the file rows' column skeleton — the Space
 * column, the data columns (empty; a folder has no writer, date or type of its own), the count where Size sits,
 * and the trailing actions slot holding a chevron instead of the two buttons.
 *
 * It borrows that skeleton rather than laying itself out because a folder row and a file row are read as one
 * list: with its own layout, the Space badge landed at a different x on each kind and the right edges did not
 * line up, which is what made the list look ragged.
 */
function FolderTableRow({
  folder,
  workspaceNames,
  showWorkspace,
  showCreatedBy,
  showDate,
  showFileColumns,
  onOpen,
}: {
  folder: FileFolderRow;
  workspaceNames: Record<string, string>;
  showWorkspace: boolean;
  showCreatedBy: boolean;
  showDate: boolean;
  showFileColumns: boolean;
  onOpen: (path: string) => void;
}) {
  const { t } = useTranslation();
  const segments = pathSegments(folder.path);
  const title = segments.length === 1 ? (workspaceNames[folder.name] ?? folder.name) : folder.name;
  const space = showWorkspace ? folderWorkspaceName(folder.path, workspaceNames) : '';

  return (
    <div
      className={styles.row}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(folder.path)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(folder.path);
        }
      }}
    >
      {/* The row IS a file row's skeleton with a folder's content: the same name column, the same data cells
          (empty — a folder has no writer, date or type), the count where Size goes, and a chevron in the actions
          slot. The empty cells carry the file cells' OWN classes, not a bespoke placeholder, so the container
          queries that drop Size, then Type, then Date hide them on exactly the same widths; a placeholder of its
          own stayed visible after its file-row twin had gone and pushed the trailing cells out of line. */}
      <span className={styles.name}>
        <span className={styles.icon}>
          <Folder size={18} aria-hidden />
        </span>
        <span className={styles.nameStack}>
          <span className={styles.nameText}>{title}</span>
        </span>
      </span>
      {showCreatedBy && <span className={`${styles.col} ${styles.colWide} ${styles.createdBy}`} />}
      {showDate && <span className={`${styles.col} ${styles.date}`} />}
      {showFileColumns && <span className={`${styles.col} ${styles.type}`} />}
      {/* What the folder holds, in the column where a file states its size — how much is in this thing, for a
          folder. One line per kind, each a glyph and a number, and a half that is zero is not drawn. The
          sentence ("1 subfolder, 3 files") stays as the accessible label, since two glyphs cannot be read
          aloud. It is a real column either way: its flex share is what puts the Space at the same x on both
          kinds of row. */}
      {showFileColumns && (
        <span className={`${styles.col} ${styles.size} ${styles.muted} ${styles.counts}`} aria-label={folderCountLabel(folder, t)}>
          {folder.folderCount > 0 && (
            <span className={styles.countLine}>
              <span className={styles.countGlyph}>
                <Folder size={12} aria-hidden />
              </span>
              {folder.folderCount}
            </span>
          )}
          {folder.fileCount > 0 && (
            <span className={styles.countLine}>
              <span className={styles.countGlyph}>
                <File size={12} aria-hidden />
              </span>
              {folder.fileCount}
            </span>
          )}
        </span>
      )}
      {showWorkspace && (
        <span className={styles.space}>
          <FilesSpaceMark id={segments[0]} name={space} />
        </span>
      )}
      <span className={styles.actions}>
        {/* In a box the size of the file rows' overflow button, so the chevron's centre lands on the ⋮'s
            rather than merely sharing its right edge — the glyphs are 16 and 30 wide. */}
        <span className={styles.chevronSlot}>
          <ChevronRight className={`${styles.chevron} dir-flip`} size={16} aria-hidden />
        </span>
      </span>
    </div>
  );
}
